import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Icon } from "semantic-ui-react";
import { FormattedMessage } from "react-intl";
import PlanerComposerInput from "./PlanerComposerInput";
import PlanerTypeSelector from "./PlanerTypeSelector";
import { createTask } from "../../../actions/planer";

/**
 * PlanerComposer : Component
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Composer component to allow the user to create new tasks
 * and add them to their planer list.
 */
class PlanerComposer extends Component {
  state = {
    /**
     * Currently selected field values
     * - composedType : Type of the new task (task/exam/...)
     * - composedText : Text content of the new task
     */
    composedType: "task",
    composedText: "",

    /**
     * True if the composer fields are visible and active
     * and false if only a placeholder button is displayed
     */
    isComposerExpanded: false
  };
  /**
   * onTypeChanged: (event, sender) => ()
   * Fired when the selected type of the new task has changed
   */
  onTypeChanged = (_, { value }) => {
    this.setState({
      composedType: value
    });
  };
  /**
   * onTextChanged: (event, sender) => ()
   * Fired when the entered text content of the new task has changed
   */
  onTextChanged = (_, { value }) => {
    this.setState({
      composedText: value
    });
  };
  /**
   * setExpanded: isExpanded => ()
   * Sets whether the composer window should be expanded or not
   * and auto-focuses the input field on expansion.
   */
  setExpanded = isExpanded => {
    this.setState(
      {
        isComposerExpanded: isExpanded
      },
      () => {
        if (isExpanded) {
          this.inputField.focus();
        }
      }
    );
  };
  /**
   * setInputFieldReferende: field => ()
   * Stores a refrence to the input field to programmatically
   * apply focus upon composer expansion.
   */
  setInputFieldReference = field => {
    this.inputField = field;
  };
  /**
   * clear: ()
   * Clears the entered task information and returns
   * to the initial state.
   */
  clear = () => {
    this.setExpanded(false);
    this.setState({
      composedType: "task",
      composedText: ""
    });
  };
  /**
   * handleFieldKeyUp: (event) => ()
   * Fired when the user has pressed a key on the keyboard
   * while typing in the input field.
   */
  handleFieldKeyUp = ({ key }) => {
    if (key === "Enter") {
      this.finishEditing();
    } else if (key === "Escape") {
      this.clear();
    }
  };
  /**
   * finishEditing: () => ()
   * Creates the task and dispatches a create action
   */
  finishEditing = () => {
    const task = {
      title: this.state.composedText.trim(),
      type: this.state.composedType
    };
    this.clear();
    if (task.title !== "") {
      this.props.createTask(task);
    }
  };
  /**
   * render: ()
   * Conditionally render the composer or display a placeholder
   * if not expanded
   */
  render() {
    if (!this.state.isComposerExpanded) {
      return (
        <div className="ag-planer-composer">
          <Button
            fluid
            onClick={() => this.setExpanded(true)}
            className="ag-planer-placeholder"
          >
            <Icon name="plus square outline" />
            <span>
              <FormattedMessage
                id="agenda.planer.tasks.create"
                description="Placeholder button to create new task in agenda"
              />
            </span>
          </Button>
        </div>
      );
    }
    return (
      <div className="ag-planer-composer expanded">
        <PlanerTypeSelector
          onChange={this.onTypeChanged}
          currentType={this.state.composedType}
        />
        <PlanerComposerInput
          onChange={this.onTextChanged}
          currentText={this.state.composedText}
          referenceInputField={this.setInputFieldReference}
          handleKeyUp={this.handleFieldKeyUp}
        />
        <Button size="mini" floated="right" compact onClick={this.clear}>
          <FormattedMessage
            id="general.controls.cancel"
            description="Cancels the current action"
          />
        </Button>
      </div>
    );
  }
}

PlanerComposer.propTypes = {
  /**
   * createTask: task => ()
   * API action connected by Redux to create a new task
   */
  createTask: PropTypes.func.isRequired
};

export default connect(
  null,
  { createTask }
)(PlanerComposer);



// WEBPACK FOOTER //
// ./src/modules/agenda/planer/PlanerComposer.js